import { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useUserSettingsContext } from 'contexts';
import { VolumeIndicator } from 'features/VolumeIndicator/components/VolumeIndicator/VolumeIndicator';
import { MicTestButton } from 'features/MicTest/components/MicTestButton/MicTestButton';
import { selectRoom, selectIsPreJoin } from 'state/selectors';
import { Section } from '../../components/Section/Section';
import { Selector } from '../../components/Selector/Selector';
import { getAudioDevicesOptions, getActiveAudioDevice } from '../../helper';
import { VolumeIndicatorWrapper, MicTestButtonWrapper } from './view';

type DeviceOption = {
    label: string;
    value: string;
};

export const MicrophoneSection = observer(() => {
    const { t } = useTranslation();
    const room = selectRoom();
    const isPreJoin = selectIsPreJoin();

    const { audioInputDevices, audioInputDeviceId, setAudioInputDeviceId, permissions } = useUserSettingsContext();
    const isMicAvailable = permissions.audio;

    const [isSwitching, setIsSwitching] = useState<boolean>(false);

    const options = useMemo(() => getAudioDevicesOptions({ devices: audioInputDevices }), [audioInputDevices]);

    const value = useMemo(() => {
        const active = getActiveAudioDevice({ devices: audioInputDevices, currentDeviceId: audioInputDeviceId });
        return active ? { label: active.label, value: active.deviceId } : null;
    }, [audioInputDevices, audioInputDeviceId]);

    const onChange = useCallback(
        (mic: DeviceOption) => {
            if (isPreJoin) {
                setAudioInputDeviceId(mic.value);
                return;
            }

            if (!room) return;

            setIsSwitching(true);
            room.switchActiveDevice('audioinput', mic.value)
                .then(() => {
                    setAudioInputDeviceId(mic.value);
                })
                .catch((e) => {
                    console.warn('Error switching audioinput device', e);
                })
                .finally(() => {
                    setIsSwitching(false);
                });
        },
        [room, isPreJoin],
    );

    const isDisabled = isSwitching || !isMicAvailable;

    return (
        <Section title={t('vks_microphone_microphone')}>
            <Selector
                isSearchable={false}
                closeMenuOnSelect={false}
                options={options}
                value={value}
                isDisabled={isDisabled}
                onChange={onChange}
                placeholder={t('vks_microphone_devices_unavailable')}
                width={'100%'}
                cy={'micsSelect'}
            />
            <VolumeIndicatorWrapper>
                <VolumeIndicator />
            </VolumeIndicatorWrapper>
            <MicTestButtonWrapper>
                <MicTestButton />
            </MicTestButtonWrapper>
        </Section>
    );
});

MicrophoneSection.displayName = 'MicrophoneSection';
